import {Element, ReplyIcon} from '@chaysehartsuff/active-html';
import {Color} from '@chaysehartsuff/active-html';
import PublicMessage from '../models/PublicMessage.js';

export default class Message extends Element {

    static get onReplyEvent() { return 'onReply'; }

    /**
     * @type {PublicMessage}
     */
    publicMessage;

    /**
     * @type {PublicMessage}
     */
    replyPublicMessage;

    /**
     * @param {PublicMessage} message
     */
    constructor(message) {
        super();
        this.publicMessage = message;

        if(message.reply_to_id) {
            this.replyPublicMessage = new PublicMessage();
            this.replyPublicMessage.id = message.reply_to_id;
            this.replyPublicMessage.get({}, this.linkReplyMessage.bind(this));
        }

        this.addClass('rounded-xl border border-white/10 bg-black/30 p-2 relative');
        this.replyOverlayKey = 'reply-icon-overlay';
        this.setId(`message_${message.id}`);

        let authorColor = message.user_id != null
            ? '#86efac'
            : '#9d9fa1';
        if(message.user_owns){
            authorColor = '#ffffff';
        }

        const authorElement = new Element()
            .addContent(message.name)
            .addClass('font-semibold inline text-[11px] tracking-[0.12em]')
            .addContent('&nbsp;')
            .addAttribute('style', `color: ${authorColor};`);

        const timeStampElement = new Element()
            .addContent(this.convertTimeToRelative(message.created_at))
            .addClass('absolute')
            .addAttribute('style', `top: 0; right: 0; font-size: 0.75rem; color: ${Color.GRAY.toString()}; letter-spacing: 0.08em;`);

        const headerElement = new Element()
            .addClass('relative block')
            .addAttribute('style', 'padding-right: 6rem;')
            .addContent(authorElement)
            .addContent(timeStampElement);

        const messageElement = new Element()
            .addContent(message.message)
            .addClass('block mt-1')
            .addAttribute('style', `color: ${Color.WHITE.toString()};`);

        this.addContent(headerElement);
        this.addContent(messageElement);

        // Hover listeners for reply overlay and glow effect
        this.onEvent('mouseenter', () => {
            this.showReplyOverlay();
            this.removeClass('border-white/10');
            this.addClass('border-white/40 shadow-sm shadow-white/20');
        });
        this.onEvent('mouseleave', () => {
            this.removeContent(this.replyOverlayKey);
            this.removeClass('border-white/40 shadow-sm shadow-white/20');
            this.addClass('border-white/10');
        });
    }

    linkReplyMessage(response){
        if(!response || !response.model){
            return;
        }

        const replyModel = response.model;
        if(!replyModel || !replyModel.id){
            return;
        }

        const targetId = `message_${replyModel.id}`;
        const replyAuthor = replyModel.name || 'unknown';

        const replyLink = new Element('a')
            .addContent(`@${replyAuthor}`)
            .addClass('text-emerald-300 hover:text-emerald-200 underline underline-offset-2 cursor-pointer')
            .setAttribute('href', `#${targetId}`)
            .onEvent('click', (e) => {
                e.preventDefault();

                const resolvedMessage = Element.createInstanceFromId(targetId);
                if (!resolvedMessage) {
                    return;
                }

                resolvedMessage.removeContent('highlight');
                const highlightOverlay = new Element()
                    .addClass('absolute inset-0 rounded-xl border-2 border-emerald-300/95 bg-emerald-300/10 shadow-[0_0_0_2px_rgba(110,231,183,0.65),0_0_28px_rgba(16,185,129,0.35)] pointer-events-none opacity-100 transition-opacity duration-[1400ms] ease-out');
                resolvedMessage.addContent(highlightOverlay, 'highlight');

                window.setTimeout(() => {
                    highlightOverlay.removeClass('opacity-100');
                    highlightOverlay.addClass('opacity-0');
                }, 30);

                window.setTimeout(() => {
                    resolvedMessage.removeContent('highlight');
                }, 1600);

                const target = document.getElementById(targetId);
                if(target){
                    target.scrollIntoView({behavior: 'smooth', block: 'center'});
                }
            });

        const replyIndicator = new Element()
            .addClass('mb-2 rounded-lg border border-emerald-300/25 bg-black/30 px-2 py-1 text-[11px] text-white/75')
            .addContent(
                new Element()
                    .addClass('flex items-center gap-1')
                    .addContent(new Element('span').addClass('text-white/50').addContent('Reply to'))
                    .addContent(replyLink)
            );

        this.removeContent('reply-indicator');
        this.addContent(replyIndicator, 'reply-indicator', 0);
    }

    showReplyOverlay() {
        const replyIconContainer = new Element()
            .addClass('absolute top-2 right-2 bg-black/80 rounded-lg p-2')
            .addContent(new ReplyIcon()
                .addClass('w-5 h-5 text-white/70 hover:text-white cursor-pointer transition-colors')
            )
            .onEvent('click', () => { this.event(Message.onReplyEvent, this); });
        
        this.addContent(replyIconContainer, this.replyOverlayKey);
    }

    convertTimeToRelative(time) {
        const date = new Date(time);
        if (Number.isNaN(date.getTime())) {
            return '';
        }

        const secondsElapsed = Math.max(1, Math.floor((Date.now() - date.getTime()) / 1000));

        const minute = 60;
        const hour = 60 * minute;
        const day = 24 * hour;
        const week = 7 * day;
        const month = 5 * week;
        const year = 365 * day;

        if (secondsElapsed < minute) {
            return this.formatRelativeUnit(Math.round(secondsElapsed), 'sec');
        }
        if (secondsElapsed < hour) {
            return this.formatRelativeUnit(Math.round(secondsElapsed / minute), 'min');
        }
        if (secondsElapsed < day) {
            return this.formatRelativeUnit(Math.round(secondsElapsed / hour), 'hour');
        }
        if (secondsElapsed < week) {
            return this.formatRelativeUnit(Math.round(secondsElapsed / day), 'day');
        }
        if (secondsElapsed < month) {
            return this.formatRelativeUnit(Math.round(secondsElapsed / week), 'week');
        }
        if (secondsElapsed < year) {
            return this.formatRelativeUnit(Math.max(1, Math.round(secondsElapsed / month)), 'month');
        }

        return this.formatRelativeUnit(Math.max(1, Math.round(secondsElapsed / year)), 'year');
    }

    formatRelativeUnit(value, unit) {
        const normalizedValue = Math.max(1, value);
        return `${normalizedValue} ${unit}${normalizedValue === 1 ? '' : 's'}`;
    }

    /**
     * 
     * @returns {PublicMessage}
     */
    getPublicMessage() {
        return this.publicMessage;
    }
}